import { isPortalUser, buildPortalUrl } from './portalLink';

/** Backend-authoritative routing block returned by /auth/login, /auth/me, etc. */
export interface AuthRouting {
  primary_role?: string | null;
  app?: 'portal' | 'website';
  allowed_apps?: string[];
  base_url?: string;
  default_path?: string;
  redirect_url?: string;
}

export interface LoginResponse {
  user?: { roles?: Array<{ name?: string; role_name?: string }> } | null;
  auth_routing?: AuthRouting;
}

export interface PostLoginTarget {
  /** true = full-page navigation to another app; false = in-app React Router navigation. */
  external: boolean;
  url: string;
}

/**
 * Decide where to send a user after a successful login.
 *
 * The backend `auth_routing.app` decision is the single source of truth for
 * role → application routing. The destination URL is built from the website's
 * OWN environment config (buildPortalUrl) so each environment redirects to the
 * correct portal host. Falls back to legacy local role mapping only when the
 * backend field is absent (e.g. an older API during rollout).
 */
export function resolvePostLoginTarget(res: LoginResponse, returnPath?: string): PostLoginTarget {
  const app = res.auth_routing?.app;

  if (app === 'portal') {
    return { external: true, url: buildPortalUrl(returnPath ?? res.auth_routing?.default_path ?? '/dashboard') };
  }
  if (app === 'website') {
    return { external: false, url: res.auth_routing?.default_path ?? '/tenant/dashboard' };
  }

  // Fallback: legacy local role mapping (kept for backward compatibility).
  if (res.user && isPortalUser(res.user)) {
    return { external: true, url: buildPortalUrl(returnPath ?? '/dashboard') };
  }
  return { external: false, url: '/tenant/dashboard' };
}

/**
 * Given an /auth/me payload, decide whether the current (website) application is
 * allowed for the user. Returns the portal path to redirect to when the website
 * is NOT an allowed app (so the caller sends them to the portal); otherwise null.
 *
 * Backend `allowed_apps` is authoritative. Returns null when the field is absent
 * (older API / rollout) so existing behaviour is preserved.
 */
export function websiteRedirectFor(
  me: { auth_routing?: AuthRouting } | null | undefined,
): string | null {
  const routing = me?.auth_routing;
  const allowed = routing?.allowed_apps;
  if (!routing || !Array.isArray(allowed) || allowed.length === 0) return null;
  if (allowed.includes('website')) return null;
  return routing.default_path ?? '/dashboard';
}
